import { LucideIcon, Carousel, useBreakpointValue, type CarouselApi } from '@skin-seoul/design-system';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { ReviewVideoPlayer } from './ReviewVideoPlayer';
import { ReviewVideoThumbnail } from './ReviewVideoThumbnail';
import type { ReviewImage } from '..';

interface ReviewImageDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  image: ReviewImage | null;
  images?: ReviewImage[];
  initialIndex?: number;
}

const MEDIA_CLASS_NAME = 'max-w-[100vw] max-h-[100dvh] object-contain';
const VIEWER_PLAY_MARK_SIZE = 56;

/** Off-slide clips stay stills: a mounted player autoplays, and nothing off-screen should. */
const ReviewVideoMedia = ({
  url,
  isSelected,
  onClick,
}: {
  url: string;
  isSelected: boolean;
  onClick: (event: React.MouseEvent) => void;
}) =>
  isSelected ? (
    <ReviewVideoPlayer url={url} className={MEDIA_CLASS_NAME} onClick={onClick} />
  ) : (
    <ReviewVideoThumbnail
      url={url}
      playMarkSize={VIEWER_PLAY_MARK_SIZE}
      className={MEDIA_CLASS_NAME}
      onClick={onClick}
    />
  );

const Overlay = ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
  <motion.div
    className="fixed top-0 left-0 right-0 bottom-0 bg-black/70 z-[999999] flex items-center justify-center"
    {...props}
  >
    {children}
  </motion.div>
);

export const ReviewImageDetailModal = ({
  isOpen,
  onClose,
  image,
  images = [],
  initialIndex = 0,
}: ReviewImageDetailModalProps) => {
  const isMobile = useBreakpointValue({ base: true, tablet: false });
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);

  const displayImages = isMobile && images.length > 0 ? images : image ? [image] : [];
  const startIndex = isMobile && images.length > 0 ? initialIndex : 0;
  /** What the non-carousel branch shows — one entry, which may be a clip rather than a photo. */
  const singleMedia = displayImages[0] ?? image;

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (carouselApi && isOpen && isMobile && images.length > 0) {
      carouselApi.scrollTo(startIndex);
    }
  }, [carouselApi, isOpen, isMobile, images.length, startIndex]);

  // Which slide is showing decides which clip is allowed to play; every player mounted at once
  // would autoplay at once, and their audio with them.
  useEffect(() => {
    if (!carouselApi) {
      return;
    }
    const readSelected = () => setSelectedIndex(carouselApi.selectedScrollSnap());
    readSelected();
    carouselApi.on('select', readSelected);
    return () => {
      carouselApi.off('select', readSelected);
    };
  }, [carouselApi]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  if (!image && displayImages.length === 0) {
    return null;
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={handleOverlayClick}
        >
          {isMobile && images.length > 1 ? (
            <Carousel
              opts={{
                align: 'center',
                loop: false,
              }}
              setApi={setCarouselApi}
            >
              <Carousel.Content
                className="w-[100vw]"
                onClickCapture={(e: React.MouseEvent) => {
                  const target = e.target as HTMLElement;
                  // A clip counts as content too: without it, pressing play on the player would
                  // register as a tap on the backdrop and close the viewer.
                  const isMediaClick = ['IMG', 'VIDEO'].includes(target.tagName) || target.closest('img, video');
                  if (!isMediaClick) {
                    onClose();
                  }
                }}
              >
                {displayImages.map((img, index) => (
                  <Carousel.Item key={img.id || index}>
                    {/* Layout wrapper only: its click handler keeps media taps from reaching the backdrop. */}
                    <div
                      role="presentation"
                      className="h-screen flex items-center justify-center"
                      onClick={handleContentClick}
                    >
                      {img.isVideo ? (
                        <ReviewVideoMedia
                          url={img.url}
                          isSelected={index === selectedIndex}
                          onClick={handleContentClick}
                        />
                      ) : (
                        // The wrapper above already stops propagation for this click.
                        <img src={img.url} alt={img.alt} className="max-w-[100vw] max-h-[100dvh] object-contain" />
                      )}
                    </div>
                  </Carousel.Item>
                ))}
              </Carousel.Content>
              <Carousel.Previous className="w-[48px] h-[48px] left-[8px] rounded-full bg-[rgba(247,247,249,0.2)] backdrop-blur-[8px] border-none hover:bg-[rgba(247,247,249,0.4)]">
                <LucideIcon name="ChevronLeft" width="36px" height="36px" className="text-neutral-50" />
              </Carousel.Previous>
              <Carousel.Next className="w-[48px] h-[48px] right-[8px] rounded-full bg-[rgba(247,247,249,0.2)] backdrop-blur-[8px] border-none hover:bg-[rgba(247,247,249,0.4)]">
                <LucideIcon name="ChevronRight" width="36px" height="36px" className="text-neutral-50" />
              </Carousel.Next>
            </Carousel>
          ) : (
            // Backdrop convenience only — Escape is the keyboard path to close (see the effect above).
            <div
              role="presentation"
              className={`flex items-center justify-center cursor-pointer ${isMobile ? 'max-w-[100vw] max-h-[100dvh] w-screen h-screen p-0' : 'max-w-[90vw] max-h-[90dvh] w-[90vw] h-[90dvh] p-4'}`}
              onClick={onClose}
            >
              {singleMedia?.isVideo ? (
                // Clicks stop here rather than reaching the backdrop, which would close the viewer
                // the moment the viewer reached for the controls.
                <ReviewVideoPlayer
                  url={singleMedia.url}
                  className={`max-w-full max-h-full h-auto object-contain ${isMobile ? 'w-screen rounded-none' : 'w-full rounded-lg'}`}
                  onClick={handleContentClick}
                />
              ) : (
                <img
                  src={singleMedia?.url}
                  alt={singleMedia?.alt}
                  className={`max-w-full max-h-full h-auto object-contain ${isMobile ? 'w-screen rounded-none' : 'w-full rounded-lg'}`}
                />
              )}
            </div>
          )}
        </Overlay>
      )}
    </AnimatePresence>
  );
};

export default ReviewImageDetailModal;
